<script setup>
  import { ref, computed, onMounted, watchEffect } from 'vue';
  import { useWindAlertStore } from '@/stores/WindAlertStore';
  import { useApiWindAlerts } from '@/composables/useApiWindAlerts';
  import { buildCarrierAddress, parseCarrierAddress } from '@/helpers/alertCarriers';
  import Toggle from './controls/Toggle.vue';
  import Icon from '@/components/icons/Icon.vue';
  import ModalAlertDevicesManageDetailViewEmail from './ModalAlertDevicesManageDetailViewEmail.vue';
  import ModalAlertDevicesManageDetailViewMobile from './ModalAlertDevicesManageDetailViewMobile.vue';
  import ModalAlertDevicesManageDetailViewIphone from './ModalAlertDevicesManageDetailViewIphone.vue';
  import ModalAlertDevicesManageDetailViewAndroid from './ModalAlertDevicesManageDetailViewAndroid.vue';
  import ModalDeleteAlertDevice from './ModalDeleteAlertDevice.vue';

  const windAlertStore = useWindAlertStore();
  const apiWindAlerts = useApiWindAlerts();

  const emits = defineEmits(['saved']);

  const isLoading = ref(false);
  const editingDevice = ref(null);
  const editingType = ref(null);
  const formName = ref('');
  const formAddress = ref('');
  const formPhone = ref('');
  const formCarrier = ref('');
  const formMaxMessages = ref(300);
  const validationErrors = ref({});
  const showDeleteModal = ref(false);
  const activeView = ref(null);

  const isSetupMode = computed(() => activeView.value !== null && editingDevice.value === null);
  const isEditMode = computed(() => activeView.value !== null && editingDevice.value !== null);
  const isAtRoot = computed(() => activeView.value === null);

  const canDelete = computed(() =>
    isEditMode.value && (editingType.value === 'E-Mail' || editingType.value === 'Mobile')
  );

  const deleteLabel = computed(() => {
    if (!editingDevice.value) return '';
    return displayType(editingDevice.value.type);
  });

  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const liveValid = computed(() => {
    const addresses = Array.isArray(formAddress.value)
      ? formAddress.value.map(a => a.trim()).filter(Boolean)
      : [];
    const addressValid = addresses.length > 0 && addresses.every(a => EMAIL_REGEX.test(a));

    const phoneDigits = String(formPhone.value || '').replace(/\D/g, '');
    const phoneValid = phoneDigits.length >= 7 && phoneDigits.length <= 15;

    const carrierValid = !!formCarrier.value;

    return { address: addressValid, phone: phoneValid, carrier: carrierValid };
  });

  function validateForm() {
    const errors = {};
    if (editingType.value === 'E-Mail') {
      const addresses = Array.isArray(formAddress.value)
        ? formAddress.value.map(a => a.trim()).filter(Boolean)
        : String(formAddress.value || '').split(',').map(a => a.trim()).filter(Boolean);
      if (!addresses.length) {
        errors.address = 'At least one email address is required';
      } else {
        const invalid = addresses.find(a => !EMAIL_REGEX.test(a));
        if (invalid) errors.address = `"${invalid}" is not a valid email address`;
      }
    }
    if (editingType.value === 'Mobile') {
      if (!formCarrier.value) errors.carrier = 'Carrier is required';
      const digits = String(formPhone.value || '').replace(/\D/g, '');
      if (digits.length < 7 || digits.length > 15) {
        errors.phone = 'A valid phone number is required';
      }
    }
    validationErrors.value = errors;
    return Object.keys(errors).length === 0;
  }

  const deviceTypeMap = {
    1: { type: 'E-Mail', icon: 'envelope' },
    2: { type: 'Mobile', icon: 'text-message' },
    3: { type: 'Android', icon: 'android-logo' },
    4: { type: 'iPhone', icon: 'apple-logo' },
  };

  const deviceIconByType = Object.values(deviceTypeMap).reduce((acc, { type, icon }) => {
    acc[type] = icon;
    return acc;
  }, {});

  const typeIdByType = Object.entries(deviceTypeMap).reduce((acc, [id, { type }]) => {
    acc[type] = parseInt(id, 10);
    return acc;
  }, {});

  const viewKeyByType = {
    'E-Mail': 'email',
    'Mobile': 'mobile',
    'Android': 'android',
    'iPhone': 'iphone',
  };

  const typeDisplayLabels = {
    'E-Mail': 'Email',
    'Mobile': 'Text Message',
    'iPhone': 'iOS',
  };

  function displayType(type) {
    return typeDisplayLabels[type] || type;
  }

  const title = computed(() => {
    if (isAtRoot.value) return 'Devices';
    if (!editingType.value) return 'Devices';
    return `${isSetupMode.value ? 'New' : 'Edit'} ${displayType(editingType.value)} Device`;
  });

  const submitLabel = computed(() => {
    if (isAtRoot.value) return '';
    return isSetupMode.value ? 'Create Device' : 'Save';
  });

  const errorCount = computed(() => Object.keys(validationErrors.value).length);
  const canSubmit = computed(() => !isAtRoot.value);

  const websiteTypes = ['E-Mail']; // 'Mobile' (text messages) is not supported at this time.
  const appTypes = ['iPhone', 'Android'];

  function deviceForType(type) {
    return (windAlertStore.alertDevices || []).find(d => d.type === type) || null;
  }

  const websiteSlots = computed(() => websiteTypes.map(type => ({ type, device: deviceForType(type) })));
  const appSlots = computed(() => appTypes.map(type => ({ type, device: deviceForType(type) })));

  function deviceIcon(device) {
    return deviceIconByType[device.type] || 'iphone';
  }

  function formatPhone(raw) {
    const digits = String(raw || '').replace(/\D/g, '').slice(-10);
    if (digits.length !== 10) return raw;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  function deviceSubText(device) {
    if (device.type === 'E-Mail') {
      const addresses = Array.isArray(device.address)
        ? device.address
        : String(device.address || '').split(',').map(a => a.trim()).filter(Boolean);
      const count = addresses.length;
      if (count === 1) return addresses[0];
      return `${count} email addresses`;
    }
    if (device.type === 'Mobile') {
      const phoneRaw = (device.address || '').split('@')[0];
      return `${device.name || ''} - ${formatPhone(phoneRaw)}`;
    }
    if (device.name && device.name !== device.type) return device.name;
    return '';
  }

  function isDeviceEnabled(device) {
    return device.enabled === true;
  }

  async function loadDevices(overrideCache = false) {
    isLoading.value = true;
    await windAlertStore.getAlertDevices(undefined, overrideCache);
    isLoading.value = false;
  }

  async function setDeviceEnabled(device, value) {
    device.enabled = value;
    await apiWindAlerts.changeAlertStatusForDevice({ alert_device_id: device.id, enabled: value });
  }

  function resetForm() {
    formName.value = '';
    formAddress.value = '';
    formPhone.value = '';
    formCarrier.value = '';
    formMaxMessages.value = 300;
  }

  function openEditView(device) {
    validationErrors.value = {};
    editingDevice.value = device;
    editingType.value = device.type;
    formName.value = device.name || '';
    formMaxMessages.value = device.max_message_count ?? 300;
    if (device.type === 'Mobile') {
      const { digits, template } = parseCarrierAddress(device.address);
      formPhone.value = formatPhone(digits);
      formAddress.value = device.address || '';
      formCarrier.value = template;
    } else if (device.type === 'E-Mail') {
      const addresses = Array.isArray(device.address)
        ? [...device.address]
        : String(device.address || '').split(',').map(a => a.trim()).filter(Boolean);
      formAddress.value = addresses.length ? addresses : [''];
      formPhone.value = '';
      formCarrier.value = '';
    } else {
      formAddress.value = device.address || '';
      formPhone.value = '';
      formCarrier.value = '';
    }
    activeView.value = viewKeyByType[device.type] || 'email';
  }

  function openSetupView(type) {
    validationErrors.value = {};
    editingDevice.value = null;
    editingType.value = type;
    resetForm();
    if (type === 'E-Mail' || type === 'Mobile') formName.value = displayType(type);
    if (type === 'E-Mail') formAddress.value = [''];
    activeView.value = viewKeyByType[type];
  }

  function openDeleteModal() {
    showDeleteModal.value = true;
  }

  async function handleDeleteConfirm() {
    if (!editingDevice.value) return;
    await apiWindAlerts.deleteAlertDevice({ device_id: editingDevice.value.id });
    showDeleteModal.value = false;
    await loadDevices(true);
    back();
  }

  function back() {
    activeView.value = null;
    editingDevice.value = null;
    editingType.value = null;
    validationErrors.value = {};
  }

  async function submit() {
    if (isAtRoot.value) return true;
    if (!validateForm()) return false;

    const payload = {
      name: formName.value,
      type_id: typeIdByType[editingType.value],
    };

    if (editingType.value === 'Mobile') {
      const digits = formPhone.value.replace(/\D/g, '');
      payload.address = buildCarrierAddress(formCarrier.value, digits);
      payload.max_message_count = Number(formMaxMessages.value) || 300;
    } else if (editingType.value === 'E-Mail') {
      const addresses = Array.isArray(formAddress.value)
        ? formAddress.value.map(a => a.trim()).filter(Boolean)
        : String(formAddress.value || '').split(',').map(a => a.trim()).filter(Boolean);
      payload.address = addresses.join(',');
    } else if (isEditMode.value) {
      payload.address = editingDevice.value.address || '';
    }

    const wasEdit = isEditMode.value;
    const deviceTypeLabel = displayType(editingType.value);

    if (wasEdit) {
      await apiWindAlerts.updateAlertDevice({ device_id: editingDevice.value.id, ...payload });
    } else {
      await apiWindAlerts.addAlertDevice(payload);
    }

    await loadDevices(true);

    if (jwc) {
      jwc.toaster.toast({
        type: 'success',
        content: `${wasEdit ? 'Saved' : 'Created'} <strong>${deviceTypeLabel}</strong> device`,
      });
    }

    emits('saved');
    back();
    return true;
  }

  onMounted(() => {
    loadDevices();
  });

  defineExpose({
    title,
    submitLabel,
    errorCount,
    canSubmit,
    isAtRoot,
    back,
    submit,
  });
</script>

<template>
  <div>
    <div v-if="isAtRoot">
      <div class="jwx-modal__section">
        <div class="jwx-modal__section-body">
          <p class="jwx-modal__section-description">
            Manage the devices that receive your wind alerts.
          </p>
        </div>
      </div>

      <div class="jwx-modal__section">
        <div class="jwx-modal__section-header">
          <h3 class="jwx-modal__section-title">Lifestyle Website Devices</h3>
        </div>
        <div class="jwx-modal__section-body">
          <ul class="jwx-block jwx-table-view" data-surface="1">
            <li v-for="slot in websiteSlots" :key="slot.type" class="jwx-table-view__row">
              <button
                type="button"
                class="jwx-table-view__action-row jwx-p-block-md"
                @click="slot.device ? openEditView(slot.device) : openSetupView(slot.type)"
              >
                <div class="jwx-table-view__row-icon">
                  <Icon :name="slot.device ? deviceIcon(slot.device) : deviceIconByType[slot.type]" />
                </div>
                <div class="jwx-table-view__row-main-content">
                  <p class="jwx-table-view__row-label jwx-font-weight-400">
                    {{ displayType(slot.type) }}
                  </p>
                  <p v-if="slot.device && deviceSubText(slot.device)" class="jwx-table-view__row-text">{{ deviceSubText(slot.device) }}</p>
                </div>
                <div class="jwx-table-view__action-row-detail">
                  <Toggle
                    v-if="slot.device"
                    :id="`device-toggle-${slot.device.id}`"
                    :modelValue="isDeviceEnabled(slot.device)"
                    :hideLabelText="true"
                    size="small"
                    @click.stop
                    @update:modelValue="(v) => setDeviceEnabled(slot.device, v)"
                  >
                    Toggle Device
                  </Toggle>
                  <span v-else class="jwx-chip" data-selected="true"><span class="jwx-chip__text jwx-chip__text--strong">Setup</span></span>
                  <Icon name="chevron-right" />
                </div>
              </button>
            </li>
          </ul>
        </div>
      </div>

      <div class="jwx-modal__section">
        <div class="jwx-modal__section-header">
          <h3 class="jwx-modal__section-title">Lifestyle App Devices</h3>
        </div>
        <div class="jwx-modal__section-body">
          <ul class="jwx-block jwx-table-view" data-surface="1">
            <li v-for="slot in appSlots" :key="slot.type" class="jwx-table-view__row">
              <button
                v-if="slot.device"
                type="button"
                class="jwx-table-view__action-row jwx-p-block-lg"
                @click="openEditView(slot.device)"
              >
                <div class="jwx-table-view__row-icon">
                  <Icon :name="deviceIcon(slot.device)" />
                </div>
                <div class="jwx-table-view__row-main-content">
                  <p class="jwx-table-view__row-label jwx-font-weight-400">
                    {{ displayType(slot.type) }}
                  </p>
                  <p v-if="deviceSubText(slot.device)" class="jwx-table-view__row-text">{{ deviceSubText(slot.device) }}</p>
                </div>
                <div class="jwx-table-view__action-row-detail">
                  <Toggle
                    :id="`device-toggle-${slot.device.id}`"
                    :modelValue="isDeviceEnabled(slot.device)"
                    :hideLabelText="true"
                    size="small"
                    @click.stop
                    @update:modelValue="(v) => setDeviceEnabled(slot.device, v)"
                  >
                    Toggle Device
                  </Toggle>
                  <Icon name="chevron-right" />
                </div>
              </button>
              <div v-else class="jwx-table-view__action-row jwx-p-block-md">
                <div class="jwx-table-view__row-icon">
                  <Icon :name="deviceIconByType[slot.type]" />
                </div>
                <div class="jwx-table-view__row-main-content">
                  <p class="jwx-table-view__row-label jwx-font-weight-400">
                    {{ displayType(slot.type) }}
                  </p>
                </div>
                <div class="jwx-table-view__action-row-detail">
                  <span class="jwx-chip jwx-chip--neutral"><span class="jwx-chip__text jwx-chip__text--strong">Setup on Device</span></span>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <ModalAlertDevicesManageDetailViewEmail
      v-else-if="activeView === 'email'"
      v-model:name="formName"
      v-model:address="formAddress"
      :errors="validationErrors"
      :valid="liveValid"
      :canDelete="canDelete"
      @delete="openDeleteModal"
    />
    <ModalAlertDevicesManageDetailViewMobile
      v-else-if="activeView === 'mobile'"
      v-model:name="formName"
      v-model:phone="formPhone"
      v-model:carrier="formCarrier"
      v-model:maxMessages="formMaxMessages"
      :errors="validationErrors"
      :valid="liveValid"
      :canDelete="canDelete"
      @delete="openDeleteModal"
    />
    <ModalAlertDevicesManageDetailViewIphone
      v-else-if="activeView === 'iphone'"
      v-model:name="formName"
    />
    <ModalAlertDevicesManageDetailViewAndroid
      v-else-if="activeView === 'android'"
      v-model:name="formName"
    />

    <ModalDeleteAlertDevice
      :show="showDeleteModal"
      :deviceLabel="deleteLabel"
      @close="showDeleteModal = false"
      @confirm="handleDeleteConfirm"
    />
  </div>
</template>
